import React, { useMemo } from "react";
import { Box, FormControl, FormLabel, MenuItem, Select } from "@mui/material";
import HPInput from "@components/common/Input";
import HPDropdown from "@components/common/Dropdown";
import HPButton from "@components/common/Button";

// Same rules as focus-level rollup name validation in RollupBuilder.tsx
const ALLOWED_CHARS = /^[a-zA-Z0-9 _\-]*$/;
const MAX_NAME_LENGTH = 100;

export interface BarOption {
  sourceLabel: string;
  displayLabel: string;
  originalLabel?: string;
}

interface CategoryRollupBuilderProps {
  barOptions: BarOption[];
  existingNames: Set<string>;
  claimedSourceLabels: Set<string>;
  name: string;
  selectedSourceLabels: string[];
  onNameChange: (name: string) => void;
  onSelectionChange: (labels: string[]) => void;
  onCancel: () => void;
  onCreate: () => void;
  isEditing?: boolean;
  itemLabel?: string;
}

const CategoryRollupBuilder: React.FC<CategoryRollupBuilderProps> = ({
  barOptions,
  existingNames,
  claimedSourceLabels,
  name,
  selectedSourceLabels,
  onNameChange,
  onSelectionChange,
  onCancel,
  onCreate,
  isEditing = false,
  itemLabel = "bars",
}) => {
  const capitalizedItemLabel =
    itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);
  const dropdownOptions = useMemo(
    () =>
      barOptions.map((bar) => ({
        value: bar.sourceLabel,
        label: bar.displayLabel,
        subtitle:
          bar.originalLabel !== undefined &&
          bar.originalLabel !== bar.displayLabel
            ? bar.originalLabel
            : undefined,
      })),
    [barOptions],
  );

  const nameError = useMemo((): string | null => {
    if (!name) return null;
    const trimmed = name.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length > MAX_NAME_LENGTH)
      return `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
    if (!ALLOWED_CHARS.test(trimmed))
      return "Use letters, numbers, spaces, hyphens, or underscores only";
    if (existingNames.has(trimmed.toLowerCase()))
      return "A category with this name already exists. Use a unique rollup name.";
    return null;
  }, [name, existingNames]);

  const crossRollupError = useMemo((): string | null => {
    const hasClaimedBar = selectedSourceLabels.some((l) =>
      claimedSourceLabels.has(l),
    );
    return hasClaimedBar
      ? `Some selected ${itemLabel} are already used in another rollup.`
      : null;
  }, [selectedSourceLabels, claimedSourceLabels, itemLabel]);

  const selectionError =
    crossRollupError ??
    (selectedSourceLabels.length > 0 && selectedSourceLabels.length < 2
      ? `Select at least 2 ${itemLabel}`
      : null);

  const isValid =
    name.trim().length > 0 &&
    !nameError &&
    selectedSourceLabels.length >= 2 &&
    !crossRollupError;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <HPInput
        label="Rollup Name*"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Enter a name for this rollup"
        error={!!nameError}
        helperText={nameError ?? ""}
      />

      <HPDropdown
        label={`Source ${capitalizedItemLabel}* (select at least 2)`}
        value={selectedSourceLabels}
        onChange={onSelectionChange}
        options={dropdownOptions}
        multiSelect
        countLabel={itemLabel}
        placeholder={`Choose ${itemLabel}`}
        error={!!selectionError}
        helperText={selectionError ?? ""}
        getOptionDisabled={(opt) => claimedSourceLabels.has(opt.value)}
        noOptionsText={`No matching ${itemLabel}`}
      />

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <HPButton variant="outlined" size="small" onClick={onCancel}>
          Cancel
        </HPButton>
        <HPButton
          variant="contained"
          size="small"
          onClick={onCreate}
          disabled={!isValid}
        >
          {isEditing ? "Save" : "Create"}
        </HPButton>
      </Box>
    </Box>
  );
};

export default CategoryRollupBuilder;
